/**
 * Ordinal scales shared by every structured tasting method the app supports.
 *
 * These are the *structural* vocabulary — the same five-step ladder underlies
 * WSET-style, CMS-grid-style and Dutch (SDEN) tasting forms, so one set of
 * enums lets a single note be rendered in any of them. Wording is ours; the
 * shapes are industry-standard and not protectable.
 */

export const FIVE_STEP = ["low", "medium-minus", "medium", "medium-plus", "high"] as const;
export type FiveStep = (typeof FIVE_STEP)[number];

export const CLARITY = ["clear", "hazy"] as const;
export type Clarity = (typeof CLARITY)[number];

export const VISUAL_INTENSITY = ["pale", "medium", "deep"] as const;
export type VisualIntensity = (typeof VISUAL_INTENSITY)[number];

export const WINE_COLOUR = ["white", "rose", "red"] as const;
export type WineColour = (typeof WINE_COLOUR)[number];

export const WHITE_HUES = ["lemon-green", "lemon", "gold", "amber", "brown"] as const;
export const ROSE_HUES = ["pink", "salmon", "orange"] as const;
export const RED_HUES = ["purple", "ruby", "garnet", "tawny", "brown"] as const;
export type Hue =
  | (typeof WHITE_HUES)[number]
  | (typeof ROSE_HUES)[number]
  | (typeof RED_HUES)[number];

/** Hues that are valid for a given colour — a "tawny white" is a data-entry error. */
export const HUES_BY_COLOUR: Readonly<Record<WineColour, readonly Hue[]>> = {
  white: WHITE_HUES,
  rose: ROSE_HUES,
  red: RED_HUES,
};

export const NOSE_CONDITION = ["clean", "unclean"] as const;
export type NoseCondition = (typeof NOSE_CONDITION)[number];

export const AROMA_INTENSITY = [
  "light",
  "medium-minus",
  "medium",
  "medium-plus",
  "pronounced",
] as const;
export type AromaIntensity = (typeof AROMA_INTENSITY)[number];

export const DEVELOPMENT = ["youthful", "developing", "fully-developed", "tired"] as const;
export type Development = (typeof DEVELOPMENT)[number];

export const SWEETNESS = [
  "dry",
  "off-dry",
  "medium-dry",
  "medium-sweet",
  "sweet",
  "luscious",
] as const;
export type Sweetness = (typeof SWEETNESS)[number];

export const ALCOHOL = ["low", "medium", "high"] as const;
export type Alcohol = (typeof ALCOHOL)[number];

export const BODY = ["light", "medium-minus", "medium", "medium-plus", "full"] as const;
export type Body = (typeof BODY)[number];

export const FINISH = ["short", "medium-minus", "medium", "medium-plus", "long"] as const;
export type Finish = (typeof FINISH)[number];

export const MOUSSE = ["delicate", "creamy", "aggressive"] as const;
export type Mousse = (typeof MOUSSE)[number];

export const QUALITY = [
  "faulty",
  "poor",
  "acceptable",
  "good",
  "very-good",
  "outstanding",
] as const;
export type Quality = (typeof QUALITY)[number];

/**
 * The four criteria a quality judgement is argued from. Each one the taster
 * can defend adds a level; see `deriveQuality`.
 */
export const QUALITY_CRITERION = ["balance", "length", "intensity", "complexity"] as const;
export type QualityCriterion = (typeof QUALITY_CRITERION)[number];

export const READINESS = [
  "too-young",
  "drink-now-will-improve",
  "drink-now-not-for-ageing",
  "too-old",
] as const;
export type Readiness = (typeof READINESS)[number];

/** Position of `value` on `scale`, 0-based; -1 if not on the scale. */
export function ordinal(scale: readonly string[], value: string): number {
  return scale.indexOf(value);
}

/** Type guard: is `value` a member of `scale`? */
export function isOn<const T extends readonly string[]>(
  scale: T,
  value: string,
): value is T[number] {
  return scale.includes(value);
}
